import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Building2, CheckCircle } from 'lucide-react';
import { TenantService } from '@/services/tenant-service';
import { useRateLimiting } from '@/hooks/use-rate-limiting';
import { toast } from '@/hooks/use-toast';

interface WorkspaceCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail: string;
  userName: string;
}

export const WorkspaceCreationModal: React.FC<WorkspaceCreationModalProps> = ({
  isOpen,
  onClose,
  userEmail,
  userName
}) => {
  const [workspaceName, setWorkspaceName] = useState('');
  const [workspaceSlug, setWorkspaceSlug] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState<'form' | 'success'>('form');

  // Rate limiting for workspace creation
  const { checkRateLimit, recordAction } = useRateLimiting({
    action: 'workspace:create',
    onRateLimitExceeded: (result) => {
      const retryAfter = result.retryAfter || 0;
      const minutes = Math.ceil(retryAfter / 60);
      toast({
        title: "Too Many Workspace Creation Attempts",
        description: `Please wait ${minutes} minutes before creating another workspace.`,
        variant: "destructive",
      });
    }
  });

  // Auto-generate slug from workspace name
  const handleWorkspaceNameChange = (value: string) => {
    setWorkspaceName(value);
    // Generate slug: lowercase, replace spaces with hyphens, remove special chars
    const slug = value
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, 30); // Limit length
    setWorkspaceSlug(slug);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!workspaceName.trim() || !workspaceSlug.trim()) {
      setError('Please enter a workspace name');
      return;
    }

    // Check rate limit before creating workspace
    const rateLimitResult = await checkRateLimit();
    if (!rateLimitResult.allowed) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create the workspace
      const workspace = await TenantService.createTenant({
        name: workspaceName.trim(),
        slug: workspaceSlug.trim(),
        domain: `${workspaceSlug.trim()}.wattfactory.tech`
      });

      if (workspace) {
        // Record successful workspace creation
        await recordAction();
        setStep('success');
        // Close modal after 3 seconds
        setTimeout(() => {
          onClose();
          // Refresh page to load new workspace
          window.location.reload();
        }, 3000);
      } else {
        setError('Failed to create workspace. Please try again.');
      }
    } catch (err) {
      // Record failed attempt
      await recordAction();
      console.error('Workspace creation error details:', err);
      let errorMessage = 'Failed to create workspace';
      
      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'object' && err !== null) {
        // Handle Supabase error objects
        const supabaseError = err as any;
        if (supabaseError.message) {
          errorMessage = supabaseError.message;
        } else if (supabaseError.error_description) {
          errorMessage = supabaseError.error_description;
        }
      }
      
      setError(`Workspace creation failed: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  if (step === 'success') {
    return (
      <Dialog open={isOpen} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <DialogTitle className="text-xl text-green-600">
              Welcome to WattFactory!
            </DialogTitle>
            <DialogDescription>
              Your workspace "{workspaceName}" has been created successfully.
              <br />
              <span className="text-sm text-muted-foreground mt-2 block">
                Access it at: <strong>{workspaceSlug}.wattfactory.tech</strong>
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              Redirecting you to your workspace...
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <Building2 className="w-6 h-6 text-blue-600" />
          </div>
          <DialogTitle className="text-xl text-center">
            Welcome, {userName}!
          </DialogTitle>
          <DialogDescription className="text-center">
            Let's create your workspace to get started with WattFactory
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="workspaceName">Workspace Name</Label>
            <Input
              id="workspaceName"
              type="text"
              placeholder="e.g., Acme Manufacturing"
              value={workspaceName}
              onChange={(e) => handleWorkspaceNameChange(e.target.value)}
              required
              disabled={loading}
              maxLength={50}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="workspaceSlug">Workspace URL</Label>
            <div className="flex items-center space-x-2">
              <Input
                id="workspaceSlug"
                type="text"
                value={workspaceSlug}
                onChange={(e) => setWorkspaceSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                required
                disabled={loading}
                maxLength={30}

              />
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                .wattfactory.tech
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              This will be your unique workspace URL
            </p>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={loading || !workspaceName.trim() || !workspaceSlug.trim()}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating workspace...
              </>
            ) : (
              <>
                <Building2 className="w-4 h-4 mr-2" />
                Create My Workspace
              </>
            )}
          </Button>

          <div className="text-center">
            <p className="text-xs text-muted-foreground">
              You'll be the admin of your workspace and can invite team members later
            </p>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}; 